<template>
    <dialog-window v-model="isOpen" data-id="deleteProfileDialog" :title="title" @close="closeDialogHandler">
        <v-form ref="form" @submit.prevent="handleSubmit">
            <form-section :is-loading="isLoading">
                <form-description :text="t('profile.deleteProfileConfirmText')" />
                <v-card-actions class="d-flex justify-end ma-0 pa-0">
                    <card-button
                        data-id="deleteProfileDialogCancelButton"
                        class="flex-1-0-0"
                        size="large"
                        :inactive-label="t('form.cancel')"
                        :disabled="isSubmitting"
                        @click="closeDialogHandler"
                    >
                        {{ t('profile.cancel') }}
                    </card-button>
                    <card-button
                        data-id="deleteProfileDialogSubmitButton"
                        type="submit"
                        class="flex-1-0-0"
                        size="large"
                        color="error"
                        variant="flat"
                        :loading="isLoading"
                        :inactive-label="t('inquiry.sendMessage')"
                        :disabled="isSubmitting"
                    >
                        {{ t('profile.deleteProfile') }}
                    </card-button>
                </v-card-actions>
            </form-section>
        </v-form>
    </dialog-window>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@geta/kolumbus-frontend/features/authentication';
import { useContainer } from '@/plugins/inversify';
import { IProfileApi, IProfileApiId } from '@/features/Profile/services/Interface/IProfileApi';
import { FormDescription, FormSection } from '@/features/common/components/form';
import { useFormHandling } from '@/features/common/components/form/composables';
import { useGlobalDialogStore } from '@/store/dialogStore';
import { IEditProfileDialog } from '../types';
import CardButton from '@/features/common/components/CardButton.vue';
import DialogWindow from '@/features/common/components/DialogWindow.vue';

const { t } = useI18n();
const userStore = useUserStore();
const container = useContainer();
const { isDialogOpen, closeDialog } = useGlobalDialogStore();
const profileApi = container.get<IProfileApi>(IProfileApiId);

const props = defineProps<IEditProfileDialog>();

const isOpen = computed(() => isDialogOpen(props.type));

const isLoading = ref(false);
const { form, isSubmitting, validateForm, toggleIsSubmitting, isFormEmpty } = useFormHandling();

const saveData = async () => {
    try {
        await profileApi.deleteProfile();
        window.location.href = userStore.claims.find(x => x.type == 'bff:logout_url')!.value;
        return true;
    } catch {
        return false;
    }
};

const submitForm = async () => {
    try {
        toggleIsSubmitting(true);
        isLoading.value = true;
        const result = await saveData();
        result && closeDialog(props.type);
    } finally {
        toggleIsSubmitting(false);
        isLoading.value = false;
    }
};

const handleSubmit = async () => {
    if (isFormEmpty()) return;
    const { valid } = await validateForm();
    if (!valid) return;
    await submitForm();
};

const closeDialogHandler = () => {
    closeDialog(props.type);
};
</script>
